/**
 * Tutorial Manager
 * Tracks which tutorial cards have been shown and provides helpers for first-visit detection
 * Tutorial state is stored per-user so it persists across logouts
 */

const TUTORIAL_STORAGE_PREFIX = 'zeal_tutorials_seen';

/**
 * Get the storage key for the current user
 */
function getTutorialStorageKey(): string {
  const userId = localStorage.getItem('zeal_user_id');
  if (!userId) {
    // Fallback to global key if no user ID (shouldn't happen in normal flow)
    return TUTORIAL_STORAGE_PREFIX;
  }
  return `${TUTORIAL_STORAGE_PREFIX}_${userId}`;
}

export type TutorialKey = 
  | 'onboarding'      // Initial onboarding (2 cards)
  | 'todayPage'       // Today/Dashboard page first visit
  | 'moonPage'        // Moon page first visit
  | 'oracle'          // Oracle page first visit
  | 'cosmicPulse'     // Cosmic Pulse modal first open
  | 'circle'          // Circle page first visit
  | 'essence'         // Essence page first visit
  | 'journey'         // Timeline/Journey page first visit
  | 'tarotPage';      // Tarot page first visit

interface TutorialState {
  [key: string]: boolean;
}

/**
 * Get all tutorial completion states for the current user
 */
export function getTutorialState(): TutorialState {
  try {
    const key = getTutorialStorageKey();
    let stored = localStorage.getItem(key);
    
    // Migration: If user-specific key doesn't exist but old global key does, migrate it
    if (!stored && key !== TUTORIAL_STORAGE_PREFIX) {
      const oldStored = localStorage.getItem(TUTORIAL_STORAGE_PREFIX);
      if (oldStored) {
        localStorage.setItem(key, oldStored);
        localStorage.removeItem(TUTORIAL_STORAGE_PREFIX);
        stored = oldStored;
      }
    }
    
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Check if a specific tutorial has been seen
 */
export function hasSeen(key: TutorialKey): boolean {
  const state = getTutorialState();
  return state[key] === true;
}

/**
 * Mark a tutorial as seen for the current user
 */
export function markSeen(key: TutorialKey): void {
  try {
    const state = getTutorialState();
    state[key] = true;
    const storageKey = getTutorialStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (err) {
    console.error('Failed to save tutorial state:', err);
  }
}

/**
 * Reset all tutorials for the current user (for testing or user-requested replay)
 */
export function resetAllTutorials(): void {
  try {
    const key = getTutorialStorageKey();
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to reset tutorials:', err);
  }
}

/**
 * Reset a specific tutorial for the current user
 */
export function resetTutorial(key: TutorialKey): void {
  try {
    const state = getTutorialState();
    delete state[key];
    const storageKey = getTutorialStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (err) {
    console.error('Failed to reset tutorial:', err);
  }
}
